/**
 * CSDDD Command Center V3 — PRODUCTION
 * Route: /csddd · Data: Neo4j graph · AI: Central agents
 * 6 Tabs: Command · Value Chain · Impacts · Grievances · Evidence Vault · Sector Intel
 * Aligned to DESIGN.md tokens: obsidian surfaces, Outfit headings, JetBrains Mono for data.
 */
import { useState, useCallback, useMemo } from "react";
import {
  Zap, Link2, Search, Megaphone, Paperclip, Brain, Landmark, Shield, Radio, Scale,
  Target, Radar, Trophy, ChevronDown, Check, ClipboardList, Camera, Satellite, Award,
  FileText, FlaskConical, Mail, Database, Map, BarChart3, Banknote, Newspaper,
  BookOpen, FolderClosed, MessageSquare, Sparkles, AlertTriangle
} from "lucide-react";
import { useCSDDDDashboard, useCSDDDScope, useCSDDDGrievances, useCSDDDEvidence } from "../hooks/useCSDDDGraph";
import { useAIQuality, useAIDraft, useAICrossMap } from "../hooks/ai_module_hooks";

// =============================================================================
// SEMANTIC TONES (DESIGN.md palette — literal class strings so Tailwind JIT sees them)
// =============================================================================

const TONE = {
  emerald: { text: "text-emerald-400", bg: "bg-emerald-500/10", bgSoft: "bg-emerald-500/5", border: "border-emerald-500/20", borderHi: "border-emerald-500/50", solid: "bg-emerald-500", glow: "shadow-[0_0_6px_rgba(16,185,129,0.25)]" },
  amber:   { text: "text-amber-400",   bg: "bg-amber-500/10",   bgSoft: "bg-amber-500/5",   border: "border-amber-500/20",   borderHi: "border-amber-500/50",   solid: "bg-amber-500",   glow: "shadow-[0_0_6px_rgba(245,158,11,0.25)]" },
  red:     { text: "text-red-400",     bg: "bg-red-500/10",     bgSoft: "bg-red-500/5",     border: "border-red-500/20",     borderHi: "border-red-500/50",     solid: "bg-red-500",     glow: "shadow-[0_0_6px_rgba(239,68,68,0.25)]" },
  orange:  { text: "text-orange-400",  bg: "bg-orange-500/10",  bgSoft: "bg-orange-500/5",  border: "border-orange-500/20",  borderHi: "border-orange-500/50",  solid: "bg-orange-500",  glow: "shadow-[0_0_6px_rgba(249,115,22,0.25)]" },
  blue:    { text: "text-blue-400",    bg: "bg-blue-500/10",    bgSoft: "bg-blue-500/5",    border: "border-blue-500/20",    borderHi: "border-blue-500/50",    solid: "bg-blue-500",    glow: "shadow-[0_0_6px_rgba(59,130,246,0.25)]" },
  zinc:    { text: "text-zinc-400",    bg: "bg-zinc-500/10",    bgSoft: "bg-zinc-500/5",    border: "border-zinc-500/20",    borderHi: "border-zinc-500/50",    solid: "bg-zinc-500",    glow: "shadow-[0_0_6px_rgba(113,113,122,0.25)]" }
};

const FOCUS = "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500";
const OVERLINE = "font-outfit text-xs font-bold uppercase tracking-widest";

const SEV = {
  1: { l: "Negligible", tone: "emerald" },
  2: { l: "Minor", tone: "zinc" },
  3: { l: "Moderate", tone: "amber" },
  4: { l: "Significant", tone: "orange" },
  5: { l: "Severe", tone: "red" }
};
const LIK = { 1: "Remote", 2: "Unlikely", 3: "Possible", 4: "Likely", 5: "Almost Certain" };
const TIER_TONE = { own: "blue", t1: "emerald", t2: "amber", t3: "orange", downstream: "zinc" };
const TIER_L = { own: "Own Operations", t1: "Tier 1", t2: "Tier 2", t3: "Tier 3+", downstream: "Downstream" };
const STATUS_TONE = { critical: "red", action_required: "amber", monitored: "blue", low_risk: "emerald" };
const SCORE_TONE = { 5: "red", 4: "orange", 3: "amber", 2: "emerald", 1: "emerald" };

const DD_STEPS = [
  { id: "integrate", n: 1, l: "Integrate", a: "Art. 7", tone: "blue", icon: Landmark },
  { id: "identify", n: 2, l: "Identify", a: "Art. 8", tone: "red", icon: Search },
  { id: "prevent", n: 3, l: "Prevent & Mitigate", a: "Art. 10-11", tone: "amber", icon: Shield },
  { id: "track", n: 4, l: "Track", a: "Art. 13", tone: "blue", icon: Radio },
  { id: "communicate", n: 5, l: "Communicate", a: "Art. 14", tone: "zinc", icon: Megaphone },
  { id: "remediate", n: 6, l: "Remediate", a: "Art. 12", tone: "emerald", icon: Scale }
];

const DOC_ICONS = {
  audit_report: ClipboardList, photo: Camera, satellite: Satellite, certificate: Award,
  policy: FileText, lab_result: FlaskConical, letter: Mail, investigation: Search,
  database: Database, geospatial: Map, survey: BarChart3, legal: Scale, financial: Banknote,
  media: Newspaper, ngo_report: BookOpen, gov_report: Landmark, internal: FolderClosed,
  grievance: MessageSquare
};

// =============================================================================
// SHARED PRIMITIVES
// =============================================================================

const Pl = ({ tone = "zinc", children, small, onClick, active, glow, testid }) => {
  const t = TONE[tone] || TONE.zinc;
  const cls = `inline-flex items-center gap-1 rounded-full border font-mono font-medium ${small ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-0.5 text-xs"} ${active ? `${t.bg} ${t.borderHi}` : `${t.bgSoft} ${t.border}`} ${t.text} ${glow ? t.glow : ""}`;
  if (onClick) {
    return <button type="button" data-testid={testid || "pill"} onClick={onClick} aria-pressed={active} className={`${cls} cursor-pointer transition-colors hover:bg-white/5 ${FOCUS}`}>{children}</button>;
  }
  return <span data-testid={testid || "pill"} className={cls}>{children}</span>;
};

const Bar = ({ v, mx, tone = "emerald", h = "h-1.5" }) => (
  <div className={`w-full ${h} overflow-hidden rounded-full bg-secondary`}>
    <div className={`h-full rounded-full ${(TONE[tone] || TONE.emerald).solid} transition-[width] duration-300 motion-reduce:transition-none`} style={{ width: `${Math.min((v / mx) * 100, 100)}%` }} />
  </div>
);

const Chevron = ({ open }) => (
  <ChevronDown aria-hidden className={`h-3.5 w-3.5 shrink-0 text-zinc-500 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
);

const DocIcon = ({ type, className = "h-3.5 w-3.5 text-zinc-400" }) => {
  const Icon = DOC_ICONS[type] || Paperclip;
  return <Icon aria-hidden className={className} />;
};

const SectionLabel = ({ tone = "zinc", children }) => (
  <div className={`${OVERLINE} ${(TONE[tone] || TONE.zinc).text} mb-2`}>{children}</div>
);

const BackButton = ({ onClick, testid, children = "← Command Center" }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`mb-3 rounded-md border border-border bg-transparent px-2.5 py-1.5 text-xs text-zinc-400 transition-colors hover:bg-white/5 hover:text-white ${FOCUS}`}>{children}</button>
);

// EvidenceCard full document inspector
function EvidenceCard({ ev, expanded, onToggle }) {
  const [viewing, setViewing] = useState(false);
  // Hooks run unconditionally (null guard inside/after) — conditional hooks crash on async ev resolution.
  const sections = useMemo(() => {
    if (!ev) return [];
    const s = [{ title: "DOCUMENT SUMMARY", content: ev.scope || ev.findings }, { title: "KEY FINDINGS", content: ev.findings }];
    if (ev.type === "audit_report") s.push({ title: "AUDIT METHODOLOGY", content: `${ev.pages || ""}-page report prepared by ${(ev.source || "").split("—")[0]?.trim()}. Non-conformances classified as Major, Moderate, Minor per audit framework.` });
    if (ev.type === "lab_result") s.push({ title: "LABORATORY METHODOLOGY", content: `Analysis under ISO 17025 by ${(ev.source || "").split("—")[0]?.trim()}. Results ±5% at 95% CI.` });
    if (ev.type === "satellite") s.push({ title: "REMOTE SENSING METHODOLOGY", content: `Source: ${ev.source}. Change detection with ground-truth validation.` });
    if (ev.type === "ngo_report") s.push({ title: "INVESTIGATION METHODOLOGY", content: `Published by ${(ev.source || "").split("—")[0]?.trim()}. Field interviews, documentary evidence, cross-referencing. Peer-reviewed.` });
    if (ev.type === "grievance") s.push({ title: "GRIEVANCE PROCEDURE", content: "Filed through external grievance mechanism per Art. 12 CSDDD and UNGPs. Registered, acknowledged within 5 business days." });
    if (ev.type === "certificate") s.push({ title: "CERTIFICATION VALIDITY", content: "Issued by accredited body. Validity and scope confirmed. Chain of custody verified." });
    if (ev.type === "legal") s.push({ title: "LEGAL STANDING", content: "Executed agreement with binding obligations. Verified by Legal Counsel. Counterparty signatures verified." });
    s.push({ title: "CHAIN OF CUSTODY", content: `Uploaded by ${ev.uploadedBy || "—"} on ${ev.uploadDate || "—"}. ${ev.verified ? `Verified by ${ev.verifiedBy} on ${ev.verifiedDate}.` : "Awaiting verification."} Hash: ${ev.hash}. Modification invalidates hash.` });
    return s;
  }, [ev]);
  if (!ev) return null;
  const isOpen = expanded === ev.id;

  return (
    <div className="mb-1">
      <button
        type="button"
        data-testid={`evidence-card-${ev.id}`}
        onClick={() => onToggle(isOpen ? null : ev.id)}
        aria-expanded={isOpen}
        className={`flex w-full items-center gap-2 border px-2.5 py-2 text-left transition-colors ${FOCUS} ${isOpen ? "rounded-t-md border-blue-500/30 bg-blue-500/5" : "rounded-md border-border bg-card hover:border-blue-500/30"}`}
      >
        <DocIcon type={ev.type} />
        <span className="flex-1 truncate text-xs font-semibold text-white">{ev.name}</span>
        <Pl tone={ev.verified ? "emerald" : "red"} small>{ev.verified ? "Verified" : "Unverified"}</Pl>
        <span className="font-mono text-[11px] text-zinc-500">{ev.size || ""}</span>
        <Chevron open={isOpen} />
      </button>
      {isOpen && (
        <div className="overflow-hidden rounded-b-md border border-t-0 border-blue-500/30 bg-card">
          <div className="flex items-center gap-2 border-b border-blue-500/20 bg-blue-500/5 px-3 py-2">
            <DocIcon type={ev.type} className="h-5 w-5 text-blue-400" />
            <div className="flex-1">
              <div className="text-xs font-bold text-white">{ev.name}</div>
              <div className="font-mono text-[11px] text-zinc-400">{ev.format} {ev.pages ? `· ${ev.pages} pages` : ""} · {ev.size || ""}</div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setViewing(!viewing); }}
              className={`rounded-md border px-3 py-1.5 text-xs font-bold transition-colors ${FOCUS} ${viewing ? "border-blue-500/50 bg-blue-500 text-black" : "border-blue-500/50 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"}`}
            >
              {viewing ? "Close" : "View"}
            </button>
          </div>
          {viewing && (
            <div className="border-b border-border bg-background px-3 py-2.5">
              <div className="mb-2 grid grid-cols-1 gap-1 sm:grid-cols-3">
                {[{ l: "Type", v: ev.type?.replace(/_/g, " ") || "" }, { l: "Source", v: ev.source || "" }, { l: "Hash", v: ev.hash || "" }].map((f, i) => (
                  <div key={i} className="rounded bg-card px-2 py-1 text-[11px]">
                    <span className="text-zinc-400">{f.l}: </span>
                    <span className={`break-all text-white ${f.l === "Hash" ? "font-mono" : ""}`}>{f.v}</span>
                  </div>
                ))}
              </div>
              {sections.map((s, i) => (
                <div key={i} className="mb-2">
                  <div className={`${OVERLINE} mb-1 text-[11px] text-blue-400`}>{s.title}</div>
                  <div className="rounded bg-card px-2 py-1.5 text-xs leading-relaxed text-zinc-200">{s.content}</div>
                </div>
              ))}
              {ev.tags && (
                <div className="flex flex-wrap gap-1">
                  {(typeof ev.tags === "string" ? ev.tags.split(",") : ev.tags || []).map((t) => <Pl key={t} tone="blue" small>{t.trim()}</Pl>)}
                </div>
              )}
            </div>
          )}
          {!viewing && (
            <div className="px-2.5 py-1.5 text-[11px] text-zinc-400">
              <strong className="text-red-400">Findings:</strong> {ev.findings}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SECTOR INTELLIGENCE DATA
// =============================================================================

const COUNTRY_RISK = { "DRC": 5, "Myanmar": 5, "Uzbekistan": 4, "Bangladesh": 4, "Vietnam": 3, "Indonesia": 4, "China": 3, "India": 3, "Brazil": 3, "Turkey": 2, "Italy": 1, "Germany": 1, "USA": 1, "Poland": 1, "Japan": 1 };
const SECTOR_RISK = {
  "Cobalt Mining": { score: 5, risks: ["Child labour in ASM", "Environmental degradation", "Community displacement", "Conflict financing"], evidence: ["RMAP smelter audit", "Mine-of-origin traceability", "Community monitoring reports", "Water quality testing"], proportionality: "OECD Minerals Guidance Annex II: Immediate cessation from worst forms. Transition to conformant. Fund alternative livelihoods." },
  "Cotton Farming": { score: 4, risks: ["Forced labour (state-sponsored)", "Child labour (seasonal)", "Water overextraction", "Pesticide exposure"], evidence: ["ILO monitoring reports", "BCI/GOTS certification", "Water stress assessment", "Worker health records"], proportionality: "OECD Garment Guidance: Transition to certified. Support ILO. Time-bound improvement 12mo." },
  "Electronics Assembly": { score: 3, risks: ["Excessive overtime", "Occupational health", "Freedom of association", "Migrant worker exploitation"], evidence: ["Payroll analysis", "H&S inspection", "Worker interviews", "Recruitment fee audits"], proportionality: "RBA Code: Self-assessment + validation. CAP. 60hr weekly limit." },
  "Palm Oil Processing": { score: 5, risks: ["Deforestation", "Indigenous land rights", "Forced/child labour", "Peatland destruction"], evidence: ["GPS polygon mapping", "Satellite deforestation analysis", "FPIC documentation", "RSPO audit"], proportionality: "RSPO + EUDR: Zero deforestation. Full FPIC. GPS traceability to plantation. Immediate suspension for verified deforestation." },
  "Chemical Processing": { score: 2, risks: ["VOC emissions", "Wastewater contamination", "Worker chemical exposure"], evidence: ["CEMS monitoring", "Discharge permits", "PPE compliance records"], proportionality: "IED + REACH: Permit compliance. BAT for emissions. Full SVHC disclosure." },
  "Steel Manufacturing": { score: 2, risks: ["GHG emissions", "Worker safety", "Air pollution"], evidence: ["EU ETS MRV", "H&S records", "CEMS data"], proportionality: "EU ETS + IED: Verified emissions. BAT compliance. Safety management system." },
  "Textile Dyeing": { score: 4, risks: ["Living wage gaps", "Fire/building safety", "Excessive overtime", "Chemical exposure"], evidence: ["Payroll vs Anker benchmark", "Structural inspection", "Overtime analysis", "ZDHC wastewater test"], proportionality: "ETI + Accord/ISA: Independent inspection. Remediation within 12mo. Worker safety committees." }
};
// Verified against Omnibus I (Directive (EU) 2026/470) and CBAM Omnibus — see memory/REGULATORY_FRAMEWORK.md (2026-07-07)
const REGS_HORIZON = [
  { reg: "CSDDD Compliance (post-Omnibus)", date: "Jul 2029", impact: "Scope 5,000 employees + €1.5B turnover; penalties up to 3% worldwide net turnover; transposition Jul 2028", status: "confirmed", severity: 5 },
  { reg: "CBAM First Declaration", date: "Sep 2027", impact: "Annual declaration for 2026 imports due 30 Sep 2027; certificate sales open Feb 2027", status: "confirmed", severity: 5 },
  { reg: "EU Forced Labour Ban", date: "Dec 2027", impact: "Products made with forced labour banned from EU single market", status: "confirmed", severity: 5 },
  { reg: "ESRS (2026) Simplified Standards", date: "FY 2027", impact: "~61% fewer mandatory datapoints; voluntary early adoption from FY 2026", status: "confirmed", severity: 4 },
  { reg: "EU Battery Regulation", date: "Feb 2027", impact: "Battery passport + critical raw material DD mandatory", status: "confirmed", severity: 4 },
  { reg: "EUDR", date: "In force", impact: "GPS polygon traceability for 7 forest-risk commodities (large operators since Dec 2025)", status: "confirmed", severity: 5 }
];
// Illustrative industry medians — NOT live peer data. Labelled as such in the UI.
const PEER_BENCH = [
  { metric: "Supplier DD coverage (Tier 1)", yours: "65%", median: "48%", best: "95%", gap: "+17% above median" },
  { metric: "Supplier DD coverage (Tier 2+)", yours: "22%", median: "15%", best: "60%", gap: "+7% above median" },
  { metric: "Adverse impacts identified", yours: "10", median: "6", best: "25+", gap: "Above median — room to deepen" },
  { metric: "Remediations active", yours: "4", median: "1", best: "8+", gap: "Strong — above most peers" },
  { metric: "Evidence docs per impact", yours: "2.0", median: "1.2", best: "4+", gap: "Good but aim for 3+" },
  { metric: "Grievance response time", yours: "18 days", median: "30 days", best: "5 days", gap: "Faster than median" },
  { metric: "DD statement published", yours: "No", median: "35% yes", best: "Annual + interim", gap: "Gap — prepare for Art. 14" }
];

// =============================================================================
// VIEWS (module scope — stable component identities, no remount-per-render)
// =============================================================================

function CommandView({ graphScope, scopeLoading, kpis, allSupps, allImps, allActs, allRems, graphGrievances, graphEvidence, drill, genDDStatement, aiDraftLoading, ddDraft, impactAI, esrsMap }) {
  const kpiCards = [
    { l: "Entities", v: kpis.totalS || allSupps.length, tone: "zinc", click: () => drill("chain") },
    { l: "Critical", v: kpis.crit || allSupps.filter((s) => s.status === "critical").length, tone: "red", click: () => drill("chain") },
    { l: "Impacts", v: kpis.totalI || allImps.length, tone: "red", sub: `${kpis.actual || allImps.filter((i) => i.type === "actual").length} actual`, click: () => drill("impact") },
    { l: "Severe(4-5)", v: kpis.sev45 || allImps.filter((i) => i.sev >= 4).length, tone: "red" },
    { l: "Actions", v: kpis.aIP || allActs.filter((a) => a.status === "in_progress").length, tone: "blue" },
    { l: "Remediations", v: kpis.totalR || allRems.length, tone: "emerald", sub: `${kpis.rIP || allRems.filter((r) => r.status === "in_progress").length} active` },
    { l: "Grievances", v: kpis.grvOpen || (graphGrievances || []).filter((g) => g.status !== "resolved").length, tone: "amber", click: () => drill("grievances") },
    { l: "Evidence", v: kpis.evTotal || (graphEvidence || []).length, tone: "blue", sub: `${kpis.evVerified || (graphEvidence || []).filter((e) => e.verified).length} verified`, click: () => drill("evidence") }
  ];
  const criticalSupps = allSupps.filter((s) => s.status === "critical");

  return (
    <div data-testid="csddd-command-view">
      <div className="mb-3 rounded-md border border-red-500/20 bg-red-500/5 px-4 py-3 text-center">
        <div className="font-outfit text-base font-bold tracking-tight text-white">See the chain. Find the harm. Fix it. Prove it.</div>
        <div className="mt-1 font-mono text-[11px] text-zinc-400">CSDDD (EU 2024/1760, amended by Omnibus I 2026/470) · Penalties up to 3% net turnover · Compliance from Jul 2029</div>
      </div>

      {/* Graph Scope */}
      {graphScope && !scopeLoading && (
        <div data-testid="csddd-scope-banner" className="mb-3 rounded-md border border-blue-500/20 bg-blue-500/5 px-4 py-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Target aria-hidden className="h-3.5 w-3.5 text-blue-400" />
            <span className={`${OVERLINE} text-blue-400`}>Materiality-Driven DD Scope</span>
            <Pl tone="emerald" small glow>Neo4j</Pl>
            <span className="ml-auto font-mono text-[11px] text-zinc-500">{graphScope.topic_count || 0} topics</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(graphScope.material_topics || []).map((t, i) => {
              const tone = t.type === "environmental" ? "emerald" : t.type === "social" ? "blue" : "amber";
              return <Pl key={i} tone={tone} small>{t.esrs_code}: {t.requirement} ({t.impact_count})</Pl>;
            })}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div data-testid="csddd-kpis" className="mb-3 grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-1.5">
        {kpiCards.map((k, i) => {
          const t = TONE[k.tone];
          const inner = (
            <>
              <div className={`font-mono text-lg font-bold leading-none ${k.tone === "zinc" ? "text-white" : t.text}`}>{k.v}</div>
              <div className="mt-1 text-xs font-semibold text-zinc-200">{k.l}</div>
              {k.sub && <div className="font-mono text-[11px] text-zinc-500">{k.sub}</div>}
            </>
          );
          const cls = `rounded-md border ${t.border} bg-card px-3 py-2.5 text-left`;
          return k.click ? (
            <button key={i} type="button" data-testid={`kpi-${k.l.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={k.click} className={`${cls} cursor-pointer transition-colors hover:border-emerald-500/50 ${FOCUS}`}>{inner}</button>
          ) : (
            <div key={i} data-testid={`kpi-${k.l.toLowerCase().replace(/[^a-z]/g, "-")}`} className={cls}>{inner}</div>
          );
        })}
      </div>

      {/* 6-Step DD */}
      <div className="mb-3 rounded-md border border-border bg-card px-4 py-3">
        <SectionLabel tone="blue">6-Step DD Process (Art. 5-16)</SectionLabel>
        <div data-testid="dd-steps" className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-6">
          {DD_STEPS.map((s) => {
            const ct = allImps.filter((i) => i.ddStep === s.id).length;
            const t = TONE[s.tone];
            const Icon = s.icon;
            return (
              <button key={s.id} type="button" onClick={() => drill("impact")} className={`rounded-md border ${t.border} bg-background px-2 py-2.5 text-center transition-colors hover:bg-white/5 ${FOCUS}`}>
                <Icon aria-hidden className={`mx-auto mb-1 h-4 w-4 ${t.text}`} />
                <div className={`text-xs font-bold ${t.text}`}>{s.l}</div>
                <div className="font-mono text-[11px] text-zinc-500">{s.a} · {ct}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* AI Panel */}
      <div data-testid="csddd-ai-panel" className="mb-3 rounded-md border border-blue-500/20 bg-blue-500/5 px-4 py-3">
        <div className="mb-2 flex items-center gap-2">
          <Sparkles aria-hidden className="h-3.5 w-3.5 text-blue-400" />
          <span className={`${OVERLINE} text-blue-400`}>AI Intelligence Layer</span>
        </div>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
          <button type="button" data-testid="ai-dd-statement-btn" onClick={genDDStatement} className={`rounded-md border border-blue-500/20 bg-card px-3 py-2.5 text-left transition-colors hover:border-blue-500/50 ${FOCUS}`}>
            <div className="mb-1 text-xs font-bold text-blue-400">DD Statement</div>
            <div className="text-[11px] text-zinc-400">Art. 14 annual disclosure</div>
            {aiDraftLoading && <div className="mt-1 font-mono text-[11px] text-amber-400">Generating…</div>}
            {ddDraft && <div className="mt-1 font-mono text-[11px] text-emerald-400">Ready ({ddDraft.word_count} words)</div>}
          </button>
          <div className="rounded-md border border-blue-500/20 bg-card px-3 py-2.5">
            <div className="mb-1 text-xs font-bold text-blue-400">Quality Gates</div>
            <div className="mb-1 font-mono text-[11px] text-zinc-400">{Object.keys(impactAI).length}/{allImps.length} checked</div>
            <Bar v={Object.keys(impactAI).length} mx={Math.max(allImps.length, 1)} tone="blue" h="h-1" />
          </div>
          <div className="rounded-md border border-blue-500/20 bg-card px-3 py-2.5">
            <div className="mb-1 text-xs font-bold text-blue-400">ESRS Map</div>
            <div className="mb-1 font-mono text-[11px] text-zinc-400">{Object.keys(esrsMap).length} mapped</div>
            <Bar v={Object.keys(esrsMap).length} mx={Math.max(allImps.length, 1)} tone="blue" h="h-1" />
          </div>
        </div>
        {ddDraft && (
          <div data-testid="dd-draft-result" className="mt-2 rounded-md border border-blue-500/30 bg-card px-3 py-2.5">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className={`${OVERLINE} text-blue-400`}>AI-Drafted DD Statement</span>
              <Pl tone="red" small glow>HUMAN APPROVAL REQUIRED</Pl>
            </div>
            <div className="max-h-40 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-zinc-200">{ddDraft.draft}</div>
            <div className="mt-1 flex gap-3 font-mono text-[11px] text-zinc-500">
              <span>Sources: {ddDraft.source_citations || 0}</span>
              <span>{ddDraft.word_count || 0} words</span>
            </div>
          </div>
        )}
      </div>

      {/* Critical Alerts */}
      <div data-testid="critical-alerts" className="rounded-md border border-red-500/20 bg-card px-4 py-3">
        <SectionLabel tone="red">Critical — immediate attention</SectionLabel>
        {criticalSupps.length === 0 ? (
          <div className="py-4 text-center text-xs text-zinc-400">No suppliers currently at critical status. New critical findings from the value chain will surface here.</div>
        ) : (
          criticalSupps.map((s) => {
            const sI = allImps.filter((i) => i.sid === s.id);
            return (
              <button key={s.id} type="button" data-testid={`critical-supplier-${s.id}`} onClick={() => drill("chain", s.id)} className={`mb-1 flex w-full flex-wrap items-center gap-2 rounded-md border border-red-500/20 bg-background px-2.5 py-2 text-left text-xs transition-colors hover:border-red-500/50 ${FOCUS}`}>
                <span className="rounded bg-red-500 px-1.5 py-0.5 font-mono text-[11px] font-bold text-white">CRITICAL</span>
                <Pl tone={TIER_TONE[s.tier]} small>{TIER_L[s.tier]}</Pl>
                <span className="font-semibold text-white">{s.name}</span>
                <span className="text-zinc-400">{s.country}</span>
                {s.esrs_codes?.length > 0 && <Pl tone="blue" small>{s.esrs_codes.length} ESRS</Pl>}
                <span className="ml-auto font-mono text-red-400">{sI.length} impacts</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function ChainView({ allSupps, allImps, allActs, allRems, supp, suppImps, selSupp, setSelSupp, tierF, setTierF, drill, expandedImp, setExpImp, expandedEv, setExpEv, expandedAct, setExpAct, expandedRem, setExpRem, evMap, impactAI, esrsMap, checkImpact, mapToESRS }) {
  const list = tierF ? allSupps.filter((s) => s.tier === tierF) : allSupps;
  const suppActs = supp ? allActs.filter((a) => suppImps.some((i) => i.actIds?.includes(a.id))) : [];

  return (
    <div data-testid="csddd-chain-view" className={`grid gap-3 ${supp ? "grid-cols-1 lg:grid-cols-[320px_1fr]" : "grid-cols-1"}`}>
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <BackButton testid="back-to-command" onClick={() => drill("command")}>← Command</BackButton>
          {Object.entries(TIER_L).map(([t, l]) => (
            <Pl key={t} tone={TIER_TONE[t]} small onClick={() => setTierF(tierF === t ? null : t)} active={tierF === t}>{l}</Pl>
          ))}
        </div>
        <div className="flex flex-col gap-1">
          {list.map((s) => {
            const isSel = selSupp === s.id;
            const sI = allImps.filter((i) => i.sid === s.id);
            const scTone = STATUS_TONE[s.status] || "zinc";
            const tt = TONE[TIER_TONE[s.tier]];
            return (
              <button
                key={s.id}
                type="button"
                data-testid={`supplier-card-${s.id}`}
                aria-pressed={isSel}
                onClick={() => { setSelSupp(isSel ? null : s.id); setExpImp(null); setExpEv(null); setExpAct(null); setExpRem(null); }}
                className={`flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${FOCUS} ${isSel ? `${tt.borderHi} ${tt.bgSoft}` : `${TONE[scTone].border} bg-card hover:bg-white/5`}`}
              >
                <Pl tone={TIER_TONE[s.tier]} small>{TIER_L[s.tier]}</Pl>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-semibold text-white">{s.name}</div>
                  <div className="truncate text-[11px] text-zinc-400">
                    {s.country} · {s.sector}
                    {sI.length > 0 && <span className="text-red-400"> · {sI.length} impacts</span>}
                  </div>
                </div>
                <Pl tone={scTone} small>{s.status?.replace(/_/g, " ")}</Pl>
              </button>
            );
          })}
        </div>
      </div>

      {/* Deep Dive */}
      {supp && (
        <div data-testid="supplier-deep-dive" className="lg:max-h-[calc(100vh-110px)] lg:overflow-y-auto">
          <div className={`mb-2 rounded-md border ${TONE[TIER_TONE[supp.tier]].borderHi} ${TONE[TIER_TONE[supp.tier]].bgSoft} px-4 py-3`}>
            <div className="mb-2 font-outfit text-base font-bold tracking-tight text-white">{supp.name}</div>
            <div className="grid grid-cols-2 gap-1.5 text-xs sm:grid-cols-3">
              {[
                { l: "Country", v: supp.country }, { l: "Tier", v: TIER_L[supp.tier] }, { l: "Sector", v: supp.sector },
                { l: "Employees", v: (supp.emp || 0).toLocaleString(), mono: true },
                { l: "Impacts", v: suppImps.length, tone: suppImps.length ? "red" : "emerald", mono: true },
                { l: "Actions", v: suppActs.length, tone: "blue", mono: true }
              ].map((f, i) => (
                <div key={i} className="rounded bg-card px-2 py-1.5">
                  <span className="text-[11px] text-zinc-400">{f.l}</span>
                  <div className={`font-semibold ${f.mono ? "font-mono" : ""} ${f.tone ? TONE[f.tone].text : "text-white"}`}>{f.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Impacts with full drill-down */}
          {suppImps.length > 0 && (
            <div className="mb-2">
              <SectionLabel tone="red">Adverse Impacts — {suppImps.length}</SectionLabel>
              {suppImps.map((imp) => {
                const isOpen = expandedImp === imp.id;
                const ddS = DD_STEPS.find((x) => x.id === imp.ddStep);
                const impActs = allActs.filter((a) => (imp.actIds || []).includes(a.id));
                const rem = imp.remId ? allRems.find((r) => r.id === imp.remId) : null;
                return (
                  <div key={imp.id} className="mb-1.5">
                    <div className={`flex items-center gap-1.5 border px-2.5 py-2 ${isOpen ? "rounded-t-md border-red-500/30 bg-red-500/5" : "rounded-md border-border bg-card"}`}>
                      <button
                        type="button"
                        data-testid={`impact-row-${imp.id}`}
                        aria-expanded={isOpen}
                        onClick={() => { setExpImp(isOpen ? null : imp.id); setExpEv(null); setExpAct(null); setExpRem(null); }}
                        className={`flex min-w-0 flex-1 items-center gap-2 text-left text-xs ${FOCUS}`}
                      >
                        <AlertTriangle aria-hidden className={`h-3.5 w-3.5 shrink-0 ${TONE[SEV[imp.sev]?.tone || "zinc"].text}`} />
                        <span className="min-w-0 flex-1 truncate font-semibold text-white">{imp.name}</span>
                        <Pl tone={imp.type === "actual" ? "red" : "amber"} small>{imp.type}</Pl>
                        <span className={`font-mono font-bold ${TONE[SEV[imp.sev]?.tone || "zinc"].text}`}>{imp.sev}x{imp.lik}={imp.sev * imp.lik}</span>
                      </button>
                      <button type="button" data-testid={`ai-check-${imp.id}`} aria-label={`Run AI quality check for ${imp.name}`} onClick={() => checkImpact(imp)} className={`rounded border px-1.5 py-0.5 font-mono text-[11px] font-semibold transition-colors ${FOCUS} ${impactAI[imp.id] ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-400" : "border-zinc-500/20 bg-zinc-500/10 text-zinc-400 hover:text-white"}`}>
                        {impactAI[imp.id] ? `AI ${impactAI[imp.id].result?.quality_score}/100` : "AI"}
                      </button>
                      <button type="button" data-testid={`esrs-map-${imp.id}`} aria-label={`Map ${imp.name} to ESRS`} onClick={() => mapToESRS(imp.id)} className={`rounded border px-1.5 py-0.5 font-mono text-[11px] font-semibold transition-colors ${FOCUS} ${esrsMap[imp.id] ? "border-blue-500/20 bg-blue-500/10 text-blue-400" : "border-zinc-500/20 bg-zinc-500/10 text-zinc-400 hover:text-white"}`}>
                        {esrsMap[imp.id] ? `${esrsMap[imp.id].length} ESRS` : "ESRS"}
                      </button>
                      <Chevron open={isOpen} />
                    </div>
                    {isOpen && (
                      <div className="rounded-b-md border border-t-0 border-red-500/30 bg-card px-3 py-2.5">
                        <div className="mb-2 grid grid-cols-2 gap-1.5 text-[11px] lg:grid-cols-4">
                          <div className="rounded bg-background px-2 py-1"><span className="text-zinc-400">Severity</span><div className={`font-mono font-bold ${TONE[SEV[imp.sev]?.tone || "zinc"].text}`}>{SEV[imp.sev]?.l} ({imp.sev})</div></div>
                          <div className="rounded bg-background px-2 py-1"><span className="text-zinc-400">Likelihood</span><div className="font-mono text-white">{LIK[imp.lik]} ({imp.lik})</div></div>
                          <div className="rounded bg-background px-2 py-1"><span className="text-zinc-400">Owner</span><div className="text-amber-400">{imp.owner}</div></div>
                          <div className="rounded bg-background px-2 py-1"><span className="text-zinc-400">DD Stage</span><div><Pl tone={ddS?.tone || "zinc"} small>{ddS?.l}</Pl></div></div>
                        </div>
                        {/* Evidence */}
                        <div className="mb-2">
                          <SectionLabel tone="blue">Evidence — {(imp.evIds || []).length} documents</SectionLabel>
                          {(imp.evIds || []).map((eid) => <EvidenceCard key={eid} ev={evMap[eid]} expanded={expandedEv} onToggle={setExpEv} />)}
                        </div>
                        {/* Actions with progress timeline */}
                        {impActs.length > 0 && (
                          <div className="mb-2">
                            <SectionLabel tone="blue">Corrective Actions — {impActs.length}</SectionLabel>
                            {impActs.map((act) => {
                              const aOpen = expandedAct === act.id;
                              return (
                                <div key={act.id} className="mb-1">
                                  <button
                                    type="button"
                                    data-testid={`action-row-${act.id}`}
                                    aria-expanded={aOpen}
                                    onClick={() => { setExpAct(aOpen ? null : act.id); setExpEv(null); }}
                                    className={`flex w-full items-center gap-1.5 border px-2.5 py-1.5 text-left text-xs transition-colors ${FOCUS} ${aOpen ? "rounded-t-md border-blue-500/30 bg-blue-500/5" : "rounded-md border-border bg-background hover:bg-white/5"}`}
                                  >
                                    <Pl tone={{ prevention: "emerald", mitigation: "amber", cessation: "red" }[act.type] || "zinc"} small>{act.type}</Pl>
                                    <span className="min-w-0 flex-1 truncate font-medium text-white">{act.name}</span>
                                    <Pl tone="blue" small>{act.status?.replace(/_/g, " ")}</Pl>
                                    <Chevron open={aOpen} />
                                  </button>
                                  {aOpen && (
                                    <div className="rounded-b-md border border-t-0 border-blue-500/30 bg-background px-2.5 py-2">
                                      <div className="mb-1.5 font-mono text-[11px] text-zinc-400">Owner: {act.owner} · Started: {act.start} · Target: {act.target}</div>
                                      {act.progress?.length > 0 && (
                                        <div className="mb-2">
                                          <div className={`${OVERLINE} mb-1 text-[11px] text-blue-400`}>Progress Timeline</div>
                                          <div className="relative pl-3.5">
                                            <div aria-hidden className="absolute bottom-1 left-1 top-1 w-0.5 rounded-full bg-blue-500/30" />
                                            {act.progress.map((p, j) => (
                                              <div key={j} className="relative mb-1.5 pl-1.5">
                                                <div aria-hidden className={`absolute -left-3 top-1 h-1.5 w-1.5 rounded-full ${j === act.progress.length - 1 ? "bg-blue-500" : "bg-zinc-600"}`} />
                                                <div className="font-mono text-[11px] text-zinc-500">{p.date}</div>
                                                <div className={`text-xs ${j === act.progress.length - 1 ? "text-white" : "text-zinc-400"}`}>{p.note}</div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      <div className={`${OVERLINE} mb-1 text-[11px] text-blue-400`}>Action Evidence</div>
                                      {(act.evIds || []).map((eid) => <EvidenceCard key={eid} ev={evMap[eid]} expanded={expandedEv} onToggle={setExpEv} />)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Remediation */}
                        {rem && (
                          <div>
                            <SectionLabel tone="emerald">Remediation</SectionLabel>
                            <button
                              type="button"
                              data-testid={`remediation-row-${rem.id}`}
                              aria-expanded={expandedRem === rem.id}
                              onClick={() => setExpRem(expandedRem === rem.id ? null : rem.id)}
                              className={`w-full border px-2.5 py-2 text-left text-xs transition-colors ${FOCUS} ${expandedRem === rem.id ? "rounded-t-md border-emerald-500/30 bg-emerald-500/5" : "rounded-md border-border bg-background hover:bg-white/5"}`}
                            >
                              <Pl tone={{ in_progress: "blue", pending: "amber" }[rem.status] || "emerald"} small>{rem.status?.replace(/_/g, " ")}</Pl>
                              <span className="ml-1.5 text-zinc-200">{rem.rem_type?.replace(/_/g, " ")} · Owner: {rem.owner} · Target: <span className="font-mono">{rem.target}</span></span>
                            </button>
                            {expandedRem === rem.id && (
                              <div className="rounded-b-md border border-t-0 border-emerald-500/30 bg-background px-2.5 py-2">
                                <div className={`${OVERLINE} mb-1 text-[11px] text-emerald-400`}>Remediation Measures</div>
                                {(rem.actions_list || []).map((a, j) => (
                                  <div key={j} className="mb-0.5 flex gap-2 rounded bg-card px-2 py-1 text-xs text-zinc-200">
                                    <Check aria-hidden className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                                    <span>{a}</span>
                                  </div>
                                ))}
                                <div className={`${OVERLINE} mb-1 mt-2 text-[11px] text-blue-400`}>Evidence</div>
                                {(rem.evIds || []).map((eid) => <EvidenceCard key={eid} ev={evMap[eid]} expanded={expandedEv} onToggle={setExpEv} />)}
                              </div>
                            )}
                          </div>
                        )}
                        {/* ESRS mappings */}
                        {esrsMap[imp.id] && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {esrsMap[imp.id].map((m, j) => <Pl key={j} tone="blue" small>{m.fw} {m.code}</Pl>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImpactView({ allImps, allSupps, drill, impactAI, checkImpact }) {
  const sorted = [...allImps].sort((a, b) => (b.sev * b.lik) - (a.sev * a.lik));
  return (
    <div data-testid="csddd-impact-view">
      <BackButton testid="impact-back-btn" onClick={() => drill("command")} />
      <SectionLabel tone="red">All Adverse Impacts — {allImps.length} ({allImps.filter((i) => i.type === "actual").length} actual)</SectionLabel>
      {sorted.map((imp) => {
        const s = allSupps.find((x) => x.id === imp.sid);
        const sevTone = SEV[imp.sev]?.tone || "zinc";
        return (
          <div key={imp.id} className={`mb-1 flex items-center gap-2 rounded-md border ${TONE[sevTone].border} bg-card px-2.5 py-2`}>
            <button type="button" data-testid={`impact-list-${imp.id}`} onClick={() => drill("chain", imp.sid)} className={`flex min-w-0 flex-1 items-center gap-2 text-left ${FOCUS}`}>
              <AlertTriangle aria-hidden className={`h-3.5 w-3.5 shrink-0 ${TONE[sevTone].text}`} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold text-white">{imp.name}</div>
                <div className="truncate text-[11px] text-zinc-400">{s?.name} · {imp.owner} · {DD_STEPS.find((x) => x.id === imp.ddStep)?.l || ""}</div>
              </div>
              <Pl tone={sevTone} small>S{imp.sev}xL{imp.lik}={imp.sev * imp.lik}</Pl>
              <Pl tone={imp.type === "actual" ? "red" : "amber"} small>{imp.type}</Pl>
            </button>
            <button type="button" aria-label={`Run AI quality check for ${imp.name}`} onClick={() => checkImpact(imp)} className={`rounded px-1.5 py-0.5 font-mono text-[11px] transition-colors ${FOCUS} ${impactAI[imp.id] ? "text-emerald-400" : "text-zinc-400 hover:text-white"}`}>
              {impactAI[imp.id] ? `AI ${impactAI[imp.id].result?.quality_score}/100` : "AI"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function GrievanceView({ graphGrievances, allImps, expandedGrv, setExpGrv, expandedEv, setExpEv, evMap, drill }) {
  const grvs = graphGrievances || [];
  return (
    <div data-testid="csddd-grievance-view">
      <BackButton testid="grievance-back-btn" onClick={() => drill("command")} />
      <SectionLabel tone="amber">Grievance Mechanism — Art. 12 · {grvs.length} cases</SectionLabel>
      {grvs.length === 0 && <div className="rounded-md border border-border bg-card py-6 text-center text-xs text-zinc-400">No grievances filed. Cases submitted through the Art. 12 mechanism will appear here with full evidence trails.</div>}
      {grvs.map((g) => {
        const isOpen = expandedGrv === g.id;
        const imp = allImps.find((i) => i.id === g.linkedImp);
        const scTone = { in_progress: "blue", pending: "amber", resolved: "emerald" }[g.status] || "zinc";
        return (
          <div key={g.id} className="mb-1.5">
            <button
              type="button"
              data-testid={`grievance-row-${g.id}`}
              aria-expanded={isOpen}
              onClick={() => { setExpGrv(isOpen ? null : g.id); setExpEv(null); }}
              className={`flex w-full flex-wrap items-center gap-2 border px-2.5 py-2 text-left text-xs transition-colors ${FOCUS} ${isOpen ? `rounded-t-md ${TONE[scTone].borderHi} ${TONE[scTone].bgSoft}` : `rounded-md ${TONE[scTone].border} bg-card hover:bg-white/5`}`}
            >
              <Pl tone={scTone} small>{g.status?.replace(/_/g, " ")}</Pl>
              <span className="min-w-0 flex-1 truncate font-semibold text-white">{g.subject}</span>
              <span className="font-mono text-[11px] text-zinc-400">Filed: {g.filed}</span>
              <span className="font-mono text-[11px] text-zinc-500">{g.updates || 0} updates</span>
              <Chevron open={isOpen} />
            </button>
            {isOpen && (
              <div className={`rounded-b-md border border-t-0 ${TONE[scTone].border} bg-card px-3 py-2.5`}>
                <div className="mb-2 grid grid-cols-1 gap-1.5 text-[11px] sm:grid-cols-2">
                  <div className="rounded bg-background px-2 py-1"><span className="text-zinc-400">Filed by: </span><span className="text-white">{g.source}</span></div>
                  <div className="rounded bg-background px-2 py-1"><span className="text-zinc-400">Assigned: </span><span className="text-amber-400">{g.assignee}</span></div>
                </div>
                {imp && (
                  <div className="mb-2 rounded border border-red-500/20 bg-red-500/5 px-2 py-1.5 text-[11px]">
                    <span className="text-zinc-400">Linked: </span>
                    <span className="font-semibold text-red-400">{imp.name}</span>
                  </div>
                )}
                <SectionLabel tone="blue">Evidence</SectionLabel>
                {(g.evIds || []).map((eid) => <EvidenceCard key={eid} ev={evMap[eid]} expanded={expandedEv} onToggle={setExpEv} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EvidenceVaultView({ graphEvidence, expandedEv, setExpEv, drill }) {
  const evs = graphEvidence || [];
  return (
    <div data-testid="csddd-evidence-view">
      <BackButton testid="evidence-back-btn" onClick={() => drill("command")} />
      <SectionLabel tone="blue">Evidence Vault — <span className="font-mono">{evs.length}</span> documents · <span className="font-mono">{evs.filter((e) => e.verified).length}</span> verified</SectionLabel>
      <div className="mb-2 text-xs text-zinc-400">Every document supporting your due diligence. Hash-sealed, timestamped, attributed. Click to inspect full provenance.</div>
      {evs.length === 0 && <div className="rounded-md border border-border bg-card py-6 text-center text-xs text-zinc-400">No evidence documents yet. Documents uploaded against impacts, actions, and grievances are vaulted here.</div>}
      {evs.map((ev) => <EvidenceCard key={ev.id} ev={ev} expanded={expandedEv} onToggle={setExpEv} />)}
    </div>
  );
}

function SectorIntelView({ graphEvidence, allImps, allSupps, allActs, allRems, drill, sectorTab, setSectorTab, simCountry, setSimCountry, simSector, setSimSector, simTier, setSimTier, simResults, runSimulation, selectedRisks, toggleRisk }) {
  const stabs = [
    { id: "simulate", l: "Risk Simulation", icon: Target },
    { id: "gaps", l: "Evidence Gaps", icon: Paperclip },
    { id: "proportionality", l: "Proportionality", icon: Scale },
    { id: "peers", l: "Peer Benchmarks", icon: Trophy },
    { id: "horizon", l: "Regulatory Horizon", icon: Radar }
  ];
  const evGaps = useMemo(() => {
    const expected = { audit_report: 4, lab_result: 3, satellite: 2, geospatial: 2, certificate: 2, grievance: 2, legal: 2, ngo_report: 2, survey: 1 };
    const have = {};
    (graphEvidence || []).forEach((e) => { have[e.type] = (have[e.type] || 0) + 1; });
    return Object.entries(expected).map(([t, n]) => ({ type: t.replace(/_/g, " "), need: n, have: have[t] || 0, gap: Math.max(n - (have[t] || 0), 0) })).sort((a, b) => b.gap - a.gap);
  }, [graphEvidence]);
  const simTone = simResults ? (SCORE_TONE[Math.round(simResults.riskScore)] || "amber") : "amber";
  const selectCls = `w-full rounded-md border border-white/10 bg-secondary/50 px-2 py-2 font-mono text-xs text-white focus:border-primary focus:ring-1 focus:ring-primary ${FOCUS}`;

  return (
    <div data-testid="csddd-sector-view">
      <BackButton testid="sector-back-btn" onClick={() => drill("command")} />
      <div className="mb-3 rounded-md border border-blue-500/20 bg-blue-500/5 px-4 py-3">
        <div className="font-outfit text-base font-bold tracking-tight text-white">Sector Intelligence & Simulation</div>
        <div className="text-xs text-zinc-400">Simulate risks, identify gaps, benchmark, scan regulatory horizon.</div>
      </div>
      <div data-testid="sector-tabs" className="mb-3 flex flex-wrap gap-1.5" role="tablist" aria-label="Sector intelligence sections">
        {stabs.map((t) => {
          const Icon = t.icon;
          const active = sectorTab === t.id;
          return (
            <button key={t.id} type="button" role="tab" aria-selected={active} data-testid={`sector-tab-${t.id}`} onClick={() => setSectorTab(t.id)} className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors ${FOCUS} ${active ? "border-blue-500/50 bg-blue-500/10 text-blue-400" : "border-border bg-transparent text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
              <Icon aria-hidden className="h-3.5 w-3.5" /> {t.l}
            </button>
          );
        })}
      </div>

      {/* Risk Simulation */}
      {sectorTab === "simulate" && (
        <div data-testid="sector-simulate">
          <SectionLabel tone="blue">Supplier Risk Simulation</SectionLabel>
          <div className="mb-3 rounded-md border border-blue-500/20 bg-card px-4 py-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]">
              <div>
                <label htmlFor="sim-country" className="mb-1 block text-[11px] text-zinc-400">Country</label>
                <select id="sim-country" data-testid="sim-country" value={simCountry} onChange={(e) => setSimCountry(e.target.value)} className={selectCls}>
                  <option value="">Select…</option>
                  {Object.keys(COUNTRY_RISK).sort().map((c) => <option key={c} value={c}>{c} ({COUNTRY_RISK[c]})</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="sim-sector" className="mb-1 block text-[11px] text-zinc-400">Sector</label>
                <select id="sim-sector" data-testid="sim-sector" value={simSector} onChange={(e) => setSimSector(e.target.value)} className={selectCls}>
                  <option value="">Select…</option>
                  {Object.keys(SECTOR_RISK).sort().map((s) => <option key={s} value={s}>{s} ({SECTOR_RISK[s].score})</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="sim-tier" className="mb-1 block text-[11px] text-zinc-400">Tier</label>
                <select id="sim-tier" data-testid="sim-tier" value={simTier} onChange={(e) => setSimTier(e.target.value)} className={selectCls}>
                  <option value="t1">Tier 1</option><option value="t2">Tier 2</option><option value="t3">Tier 3+</option>
                </select>
              </div>
              <div className="flex items-end">
                <button type="button" data-testid="sim-run-btn" onClick={runSimulation} disabled={!simCountry || !simSector} className={`w-full rounded-md bg-primary px-4 py-2 text-xs font-bold text-black shadow-[0_0_10px_rgba(16,185,129,0.2)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto ${FOCUS}`}>
                  Simulate →
                </button>
              </div>
            </div>
          </div>
          {simResults && (
            <div data-testid="sim-results" className={`rounded-md border ${TONE[simTone].borderHi} bg-card px-4 py-3.5`}>
              <div className="mb-3 flex items-center gap-3">
                <div className={`flex h-12 w-12 items-center justify-center rounded-md border-2 ${TONE[simTone].borderHi} ${TONE[simTone].bg} font-mono text-lg font-bold ${TONE[simTone].text}`}>{simResults.riskScore}</div>
                <div>
                  <div className="font-outfit text-sm font-bold text-white">{simResults.riskScore >= 4 ? "HIGH — Enhanced DD" : simResults.riskScore >= 3 ? "MEDIUM — Standard DD" : "LOW — Simplified DD"}</div>
                  <div className="font-mono text-[11px] text-zinc-400">Country: {simResults.countryRisk}/5 x Sector: {simResults.sectorRisk}/5 x Tier: {simResults.tierMultiplier}x</div>
                </div>
              </div>
              <div className="mb-2">
                <SectionLabel tone="red">Predicted Impacts</SectionLabel>
                {simResults.risks.map((r, i) => {
                  const chk = selectedRisks.has(r);
                  return (
                    <button key={i} type="button" role="checkbox" aria-checked={chk} onClick={() => toggleRisk(r)} className={`mb-0.5 flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors ${FOCUS} ${chk ? "border-red-500/30 bg-red-500/5" : "border-border bg-background hover:bg-white/5"}`}>
                      <span aria-hidden className={`flex h-4 w-4 items-center justify-center rounded border ${chk ? "border-red-400 bg-red-500/20" : "border-zinc-600 bg-transparent"}`}>
                        {chk && <Check className="h-3 w-3 text-red-400" />}
                      </span>
                      <span className="flex-1 text-xs text-zinc-200">{r}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mb-2">
                <SectionLabel tone="blue">Evidence Needed</SectionLabel>
                {simResults.evidence.map((e, i) => (
                  <div key={i} className="mb-0.5 flex items-center gap-2 rounded bg-background px-2.5 py-1.5 text-xs text-zinc-200">
                    <Paperclip aria-hidden className="h-3.5 w-3.5 text-blue-400" />{e}
                  </div>
                ))}
              </div>
              <div className="rounded-md border border-blue-500/20 bg-blue-500/5 px-3 py-2">
                <SectionLabel tone="blue">Proportionate Response</SectionLabel>
                <div className="text-xs leading-relaxed text-zinc-200">{simResults.proportionality}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Evidence Gaps */}
      {sectorTab === "gaps" && (
        <div data-testid="sector-gaps">
          <SectionLabel tone="blue">Evidence Gap Analysis</SectionLabel>
          <div className="mb-2 text-[11px] text-zinc-500">Expected volumes are a heuristic baseline from sector guidance, compared against your vaulted evidence.</div>
          {evGaps.map((g, i) => (
            <div key={i} className={`mb-1 flex items-center gap-2.5 rounded-md border ${g.gap > 0 ? "border-red-500/20" : "border-emerald-500/20"} bg-card px-3 py-2`}>
              {g.gap > 0 ? <AlertTriangle aria-hidden className="h-3.5 w-3.5 text-red-400" /> : <Check aria-hidden className="h-3.5 w-3.5 text-emerald-400" />}
              <span className="flex-1 text-xs font-semibold capitalize text-white">{g.type}</span>
              <span className="text-[11px] text-zinc-400">Have: <strong className={`font-mono ${g.have >= g.need ? "text-emerald-400" : "text-amber-400"}`}>{g.have}</strong> / Need: <strong className="font-mono text-white">{g.need}</strong></span>
              <div className="w-16"><Bar v={g.have} mx={g.need} tone={g.have >= g.need ? "emerald" : "amber"} h="h-1" /></div>
              {g.gap > 0 && <span className="font-mono text-[11px] font-semibold text-red-400">-{g.gap}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Proportionality */}
      {sectorTab === "proportionality" && (
        <div data-testid="sector-proportionality">
          <SectionLabel tone="blue">Proportionality — Art. 6</SectionLabel>
          {allImps.filter((i) => i.sev >= 4).map((imp) => {
            const s = allSupps.find((x) => x.id === imp.sid);
            const impActs = allActs.filter((a) => (imp.actIds || []).includes(a.id));
            const rem = imp.remId ? allRems.find((r) => r.id === imp.remId) : null;
            const hasAct = impActs.length > 0 || !!rem;
            const hasEv = (imp.evIds || []).length >= 2;
            const hasRem = !!rem;
            const hasProg = impActs.some((a) => (a.progress || []).length > 2);
            const score = Math.min((hasAct ? 40 : 0) + (hasEv ? 30 : 0) + (hasRem ? 20 : 0) + (hasProg ? 10 : 0), 100);
            const scoreTone = score >= 70 ? "emerald" : score >= 40 ? "amber" : "red";
            const sevTone = SEV[imp.sev]?.tone || "zinc";
            return (
              <div key={imp.id} data-testid={`proportionality-${imp.id}`} className={`mb-2 rounded-md border ${TONE[sevTone].border} bg-card px-4 py-3`}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className={`rounded px-1.5 py-0.5 font-mono text-xs font-bold ${TONE[sevTone].bg} ${TONE[sevTone].text}`}>Sev {imp.sev}</span>
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-white">{imp.name}</span>
                  <button type="button" onClick={() => drill("chain", imp.sid)} className={`text-[11px] text-zinc-400 transition-colors hover:text-white ${FOCUS}`}>{s?.name} →</button>
                </div>
                <div className="mb-2 flex items-center gap-2">
                  <span className="min-w-[70px] text-[11px] text-zinc-400">Score:</span>
                  <div className="flex-1"><Bar v={score} mx={100} tone={scoreTone} h="h-2" /></div>
                  <span className={`font-mono text-sm font-bold ${TONE[scoreTone].text}`}>{score}%</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {[
                    { l: "Action Plan", ok: hasAct, pts: hasAct ? 40 : 0, tone: "blue" },
                    { l: "Evidence >=2", ok: hasEv, pts: hasEv ? 30 : 0, tone: "blue" },
                    { l: "Remediation", ok: hasRem, pts: hasRem ? 20 : 0, tone: "emerald" },
                    { l: "Progress 3+", ok: hasProg, pts: hasProg ? 10 : 0, tone: "blue" }
                  ].map((cr, j) => (
                    <span key={j} className={`rounded border px-2 py-0.5 font-mono text-[11px] font-semibold ${cr.ok ? `${TONE[cr.tone].bg} ${TONE[cr.tone].border} ${TONE[cr.tone].text}` : "border-red-500/20 bg-red-500/5 text-red-400"}`}>
                      {cr.ok ? "✓" : "○"} {cr.l} ({cr.pts}pts)
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Peer Benchmarks */}
      {sectorTab === "peers" && (
        <div data-testid="sector-peers">
          <SectionLabel tone="amber">Peer Benchmarks</SectionLabel>
          <div className="mb-2 flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-400">
            <AlertTriangle aria-hidden className="h-3.5 w-3.5 shrink-0" />
            Illustrative industry figures — not yet connected to live peer or platform data. Do not cite in disclosures.
          </div>
          {PEER_BENCH.map((pb, i) => (
            <div key={i} className="mb-1 rounded-md border border-border bg-card px-3 py-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="min-w-0 flex-1 text-xs font-semibold text-white">{pb.metric}</span>
                <div className="min-w-[52px] text-center"><div className="text-[11px] uppercase tracking-wider text-zinc-500">You</div><div className="font-mono text-xs font-bold text-blue-400">{pb.yours}</div></div>
                <div className="min-w-[52px] text-center"><div className="text-[11px] uppercase tracking-wider text-zinc-500">Median</div><div className="font-mono text-xs text-zinc-400">{pb.median}</div></div>
                <div className="min-w-[52px] text-center"><div className="text-[11px] uppercase tracking-wider text-zinc-500">Best</div><div className="font-mono text-xs text-emerald-400">{pb.best}</div></div>
              </div>
              <div className={`mt-1 text-[11px] ${pb.gap.includes("+") || pb.gap.includes("Strong") || pb.gap.includes("Good") || pb.gap.includes("Faster") ? "text-emerald-400" : "text-amber-400"}`}>{pb.gap}</div>
            </div>
          ))}
        </div>
      )}

      {/* Regulatory Horizon */}
      {sectorTab === "horizon" && (
        <div data-testid="sector-horizon">
          <SectionLabel tone="blue">Regulatory Horizon</SectionLabel>
          {[...REGS_HORIZON].sort((a, b) => b.severity - a.severity).map((r, i) => {
            const tone = { 5: "red", 4: "orange", 3: "amber" }[r.severity] || "amber";
            return (
              <div key={i} className={`mb-1.5 rounded-md border ${TONE[tone].border} bg-card px-3.5 py-2.5`}>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className={`rounded px-2 py-0.5 font-mono text-[11px] font-bold ${TONE[tone].bg} ${TONE[tone].text}`}>{r.severity >= 5 ? "CRITICAL" : r.severity >= 4 ? "HIGH" : "MEDIUM"}</span>
                  <span className="min-w-0 flex-1 text-xs font-bold text-white">{r.reg}</span>
                  <Pl tone={r.status === "confirmed" ? "emerald" : "amber"} small>{r.status}</Pl>
                  <span className="font-mono text-xs font-bold text-blue-400">{r.date}</span>
                </div>
                <div className="text-xs text-zinc-400">{r.impact}</div>
              </div>
            );
          })}
          <div className="mt-2 rounded-md border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-zinc-200">
            <span className="font-bold text-red-400">Key deadlines: </span>CBAM declaration for 2026 imports due 30 Sep 2027. CSDDD compliance from 26 Jul 2029 — penalties up to 3% of worldwide net turnover under national regimes (Omnibus I removed the EU-wide civil liability framework).
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN
// =============================================================================

const NAV = [
  { id: "command", l: "Command Center", icon: Zap },
  { id: "chain", l: "Value Chain", icon: Link2 },
  { id: "impact", l: "Impacts", icon: Search },
  { id: "grievances", l: "Grievances", icon: Megaphone },
  { id: "evidence", l: "Evidence Vault", icon: Paperclip },
  { id: "sector", l: "Sector Intelligence", icon: Brain }
];

export default function CSDDDCommandCenterV3() {
  const [depth, setDepth] = useState("command");
  const [selSupp, setSelSupp] = useState(null);
  const [expandedImp, setExpImp] = useState(null);
  const [expandedEv, setExpEv] = useState(null);
  const [expandedAct, setExpAct] = useState(null);
  const [expandedRem, setExpRem] = useState(null);
  const [expandedGrv, setExpGrv] = useState(null);
  const [tierF, setTierF] = useState(null);
  const [sectorTab, setSectorTab] = useState("simulate");
  const [simCountry, setSimCountry] = useState("");
  const [simSector, setSimSector] = useState("");
  const [simTier, setSimTier] = useState("t1");
  const [simResults, setSimResults] = useState(null);
  const [selectedRisks, setSelectedRisks] = useState(new Set());

  // Graph Data
  const { data: dashboard, loading: dashLoading } = useCSDDDDashboard();
  const { scope: graphScope, loading: scopeLoading } = useCSDDDScope();
  const { grievances: graphGrievances } = useCSDDDGrievances();
  const { evidence: graphEvidence } = useCSDDDEvidence();

  // AI Hooks
  const { check: aiQuality } = useAIQuality("csddd");
  const { generate: aiDraft, loading: aiDraftLoading } = useAIDraft("csddd");
  const { suggest: aiCrossmap } = useAICrossMap("csddd");
  const [impactAI, setImpactAI] = useState({});
  const [ddDraft, setDDDraft] = useState(null);
  const [esrsMap, setEsrsMap] = useState({});

  // Derived from graph
  const allSupps = useMemo(() => {
    if (!dashboard?.suppliers) return [];
    return dashboard.suppliers.map((r) => {
      const s = r.s ? { ...r.s } : r;
      s.impacts = (r.impacts || []).filter((i) => i && i.id).map((i) => {
        const imp = typeof i === "object" ? { ...i } : i;
        try { imp.evIds = JSON.parse(imp.evIds || "[]"); } catch { imp.evIds = []; }
        try { imp.actIds = JSON.parse(imp.actIds || "[]"); } catch { imp.actIds = []; }
        return imp;
      });
      s.actions = (r.actions || []).filter((a) => a && a.id).map((a) => {
        const act = { ...a };
        try { act.progress = JSON.parse(act.progress || "[]"); } catch { act.progress = []; }
        try { act.evIds = JSON.parse(act.evIds || "[]"); } catch { act.evIds = []; }
        return act;
      });
      s.remediations = (r.remediations || []).filter((rm) => rm && rm.id).map((rm) => {
        const rem = { ...rm };
        try { rem.actions_list = JSON.parse(rem.actions_list || "[]"); } catch { rem.actions_list = []; }
        try { rem.evIds = JSON.parse(rem.evIds || "[]"); } catch { rem.evIds = []; }
        return rem;
      });
      s.esrs_codes = (r.esrs_codes || []).filter(Boolean);
      s.emp = s.employees || s.emp || 0;
      s.risk = s.risk_score || s.risk || 0;
      return s;
    });
  }, [dashboard]);
  const allImps = useMemo(() => allSupps.flatMap((s) => (s.impacts || []).map((i) => ({ ...i, sid: s.id }))), [allSupps]);
  const allActs = useMemo(() => allSupps.flatMap((s) => s.actions || []), [allSupps]);
  const allRems = useMemo(() => allSupps.flatMap((s) => s.remediations || []), [allSupps]);
  const evMap = useMemo(() => { const m = {}; (graphEvidence || []).forEach((e) => { m[e.id] = e; }); return m; }, [graphEvidence]);
  const kpis = useMemo(() => dashboard?.kpis || {}, [dashboard]);
  const supp = useMemo(() => (selSupp ? allSupps.find((s) => s.id === selSupp) : null), [selSupp, allSupps]);
  const suppImps = useMemo(() => (supp ? allImps.filter((i) => i.sid === supp.id) : []), [supp, allImps]);

  // AI Actions
  const checkImpact = useCallback(async (imp) => { const r = await aiQuality({ datapoint_id: imp.id, value: String(imp.sev), unit: "severity" }); if (r?.result) setImpactAI((p) => ({ ...p, [imp.id]: r })); }, [aiQuality]);
  const genDDStatement = useCallback(async () => { const ids = allImps.filter((i) => i.sev >= 3).map((i) => i.id); const r = await aiDraft({ framework_id: "csddd_art14", section_id: "dd_statement", datapoint_ids: ids, tone: "formal" }); if (r?.result) setDDDraft(r.result); }, [aiDraft, allImps]);
  const mapToESRS = useCallback(async (impId) => { const r = await aiCrossmap({ datapoint_id: impId }); if (r?.result?.mappings) setEsrsMap((p) => ({ ...p, [impId]: r.result.mappings })); }, [aiCrossmap]);

  const drill = useCallback((d, s) => { setDepth(d); if (s !== undefined) setSelSupp(s); setExpImp(null); setExpEv(null); setExpAct(null); setExpRem(null); setExpGrv(null); }, []);
  const toggleRisk = useCallback((r) => { setSelectedRisks((p) => { const n = new Set(p); n.has(r) ? n.delete(r) : n.add(r); return n; }); }, []);
  const runSimulation = useCallback(() => {
    const cr = COUNTRY_RISK[simCountry] || 2;
    const sr = SECTOR_RISK[simSector];
    if (!sr) { setSimResults({ riskScore: cr, risks: ["General risks"], evidence: ["Questionnaire"], proportionality: "Standard DD." }); return; }
    const tm = simTier === "t1" ? 1 : simTier === "t2" ? 1.3 : 1.6;
    setSimResults({ riskScore: Math.min(Math.round((cr * 0.4 + sr.score * 0.4 + tm * 2) * 10) / 10, 5), countryRisk: cr, sectorRisk: sr.score, risks: sr.risks, evidence: sr.evidence, proportionality: sr.proportionality, tierMultiplier: tm });
    setSelectedRisks(new Set(sr.risks.slice(0, cr >= 4 ? sr.risks.length : 2)));
  }, [simCountry, simSector, simTier]);

  return (
    <div data-testid="csddd-command-center-v3" className="dark min-h-screen bg-background font-sans text-foreground">
      <div className="sticky top-0 z-20 border-b border-border bg-black/60 px-4 pb-1.5 pt-2 backdrop-blur-xl">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md border border-red-500/50 bg-red-500/10 font-mono text-[11px] font-bold text-red-400">DD</div>
          <span className="font-outfit text-sm font-bold text-white">DisclosureOS</span>
          <span className="text-zinc-600">/</span>
          <span className="font-outfit text-sm font-bold text-red-400">Due Diligence OS</span>
          <div className="flex-1" />
          {dashLoading && <span className="font-mono text-[11px] text-amber-400">Loading…</span>}
          <Pl tone="emerald" small glow>Neo4j</Pl>
          <Pl tone="blue" small>AI</Pl>
        </div>
        <div data-testid="csddd-nav-tabs" className="flex flex-wrap gap-1.5" role="tablist" aria-label="CSDDD sections">
          {NAV.map((v) => {
            const Icon = v.icon;
            const active = depth === v.id;
            return (
              <button key={v.id} type="button" role="tab" aria-selected={active} data-testid={`nav-tab-${v.id}`} onClick={() => drill(v.id)} className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors ${FOCUS} ${active ? "border-red-500/50 bg-red-500/10 text-red-400" : "border-border bg-transparent text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
                <Icon aria-hidden className="h-3.5 w-3.5" /> {v.l}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 pb-8 pt-3">
        {depth === "command" && <CommandView graphScope={graphScope} scopeLoading={scopeLoading} kpis={kpis} allSupps={allSupps} allImps={allImps} allActs={allActs} allRems={allRems} graphGrievances={graphGrievances} graphEvidence={graphEvidence} drill={drill} genDDStatement={genDDStatement} aiDraftLoading={aiDraftLoading} ddDraft={ddDraft} impactAI={impactAI} esrsMap={esrsMap} />}
        {depth === "chain" && <ChainView allSupps={allSupps} allImps={allImps} allActs={allActs} allRems={allRems} supp={supp} suppImps={suppImps} selSupp={selSupp} setSelSupp={setSelSupp} tierF={tierF} setTierF={setTierF} drill={drill} expandedImp={expandedImp} setExpImp={setExpImp} expandedEv={expandedEv} setExpEv={setExpEv} expandedAct={expandedAct} setExpAct={setExpAct} expandedRem={expandedRem} setExpRem={setExpRem} evMap={evMap} impactAI={impactAI} esrsMap={esrsMap} checkImpact={checkImpact} mapToESRS={mapToESRS} />}
        {depth === "impact" && <ImpactView allImps={allImps} allSupps={allSupps} drill={drill} impactAI={impactAI} checkImpact={checkImpact} />}
        {depth === "grievances" && <GrievanceView graphGrievances={graphGrievances} allImps={allImps} expandedGrv={expandedGrv} setExpGrv={setExpGrv} expandedEv={expandedEv} setExpEv={setExpEv} evMap={evMap} drill={drill} />}
        {depth === "evidence" && <EvidenceVaultView graphEvidence={graphEvidence} expandedEv={expandedEv} setExpEv={setExpEv} drill={drill} />}
        {depth === "sector" && <SectorIntelView graphEvidence={graphEvidence} allImps={allImps} allSupps={allSupps} allActs={allActs} allRems={allRems} drill={drill} sectorTab={sectorTab} setSectorTab={setSectorTab} simCountry={simCountry} setSimCountry={setSimCountry} simSector={simSector} setSimSector={setSimSector} simTier={simTier} setSimTier={setSimTier} simResults={simResults} runSimulation={runSimulation} selectedRisks={selectedRisks} toggleRisk={toggleRisk} />}
      </div>
    </div>
  );
}
